// Показ времени. Правило 11: данные прошлой сессии показываются мгновенно с пометкой
// времени — эта пометка и собирается здесь. Даты — ДД.ММ.ГГГГ, как в документах клиента.

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

/** «15.08.2026» */
export function formatDateDMY(date: Date): string {
  return `${pad2(date.getDate())}.${pad2(date.getMonth() + 1)}.${date.getFullYear()}`;
}

/**
 * Календарная дата с сервера («2026-08-15») → «15.08.2026». Разбирается ТЕКСТОМ, а не через
 * `new Date`: конструктор читает такую строку как полночь UTC, и западнее Гринвича дата
 * съезжает на день назад. Дата приёма и дата увольнения — юридические факты, съезжать им
 * нельзя. Пусто или мусор — прочерк (правило 7: неизвестное отличается от нуля).
 */
export function formatDateOnly(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (match === null) {
    return value;
  }
  return `${match[3]}.${match[2]}.${match[1]}`;
}

/**
 * Последний день периода, у которого сервер отдаёт конец НЕ включительно: «2026-10-01» →
 * «30.09.2026».
 *
 * Сервер хранит период полуоткрытым — от первого числа до первого числа следующего месяца, — и
 * это верно для расчёта. Человеку так писать нельзя: сводка первого экрана была подписана
 * «01.09.2026 — 01.10.2026», то есть обещала и первое октября (проход по боевым данным
 * 23.09.2026). Вычитается день тоже без `new Date` от строки — по той же причине, что выше.
 */
export function formatLastDayBefore(value: string | null | undefined): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? '');
  if (match === null) {
    return formatDateOnly(value);
  }
  const накануне = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]) - 1));
  return `${pad2(накануне.getUTCDate())}.${pad2(накануне.getUTCMonth() + 1)}.${накануне.getUTCFullYear()}`;
}

/**
 * Следующий день после календарной даты: «2026-09-30» → «01.10.2026».
 *
 * Нужен фразе о последствии даты «По»: последний день назначения ВКЛЮЧИТЕЛЬНЫЙ, и человек должен
 * прочитать, с какого дня его НЕ будет в табеле. 10.10.2026 заказчик понял «По 30.09» как «период
 * работы» и потерял тридцать человек из октября: слово «по» само по себе этого не говорит.
 * Разбор через UTC, как у `formatLastDayBefore`: от местного пояса день сдвигаться не должен.
 */
export function formatDayAfter(value: string | null | undefined): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? '');
  if (match === null) {
    return formatDateOnly(value);
  }
  const назавтра = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]) + 1));
  return `${pad2(назавтра.getUTCDate())}.${pad2(назавтра.getUTCMonth() + 1)}.${назавтра.getUTCFullYear()}`;
}

/** «14:32» */
export function formatTimeHM(date: Date): string {
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Момент с сервера (ISO-строка) → Date. Пусто или мусор — null, а не «Invalid Date»:
 * пометка среза тогда просто не показывается, вместо «данные на NaN:NaN».
 */
export function parseMoment(value: string | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Пометка среза данных: сегодняшний срез — «данные на 14:32», вчерашний и старше —
 * с датой: «данные на 14.08.2026, 21:07». Голое время у вчерашнего среза врало бы:
 * человек прочтёт его как сегодняшнее.
 */
export function formatDataAsOf(sliceAt: Date, now: Date = new Date()): string {
  if (isSameDay(sliceAt, now)) {
    return `данные на ${formatTimeHM(sliceAt)}`;
  }
  return `данные на ${formatDateDMY(sliceAt)}, ${formatTimeHM(sliceAt)}`;
}
